import { useState, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence } from 'motion/react';
import { C } from '../../constants/colors.js';
import { Btn } from '../ui/Btn.jsx';
import { markStoryRead } from '../../lib/stories.js';
import { ParentCoachingPrompt } from '../parent/ParentCoachingPrompt.jsx';
import { getNextCoachingPrompt, logCoachingDone } from '../../lib/coachingPrompts.js';
import { useTTS } from '../../hooks/useTTS.js';

const SPEED_OPTIONS=[{id:'slow',label:'Slow',rate:0.7},{id:'normal',label:'Normal',rate:0.85},{id:'fast',label:'Fast',rate:0.9}];

export function StoryReading({story,profile,onBack,coachingPromptsEnabled=true}){
  const isBedtimeAutoRead = !!story?.autoRead;
  const[paraIdx,setParaIdx]=useState(0);const[speaking,setSpeaking]=useState(!!story?.autoRead);const[done,setDone]=useState(false);const[hlSent,setHlSent]=useState(-1);const[speedIdx,setSpeedIdx]=useState(1);const speedRef=useRef(1);
  const { speak, stop } = useTTS();
  const[coachingPrompt,setCoachingPrompt]=useState(null);const[showCoaching,setShowCoaching]=useState(false);
  const age=profile?.age||7;const fontSize=age<=6?28:age<=9?24:22;
  const paragraphs=useMemo(()=>{
    const parsed=(story?.story||'').split('\n\n').filter(Boolean);
    return parsed.length>0?parsed:['This story is on its way... check back at bedtime! 🌙'];
  },[story?.story]);
  const curPara=paragraphs[paraIdx]||'';
  useEffect(()=>{return()=>{stop();};},[stop]);
  useEffect(()=>{if(done)try{markStoryRead(profile?.id);}catch{};},[done]);
  useEffect(()=>{
    if(!done||!coachingPromptsEnabled)return undefined;
    const timer=setTimeout(()=>{
      const prompt=getNextCoachingPrompt();
      if(prompt){setCoachingPrompt(prompt);setShowCoaching(true);}
    },500);
    return()=>clearTimeout(timer);
  },[done,coachingPromptsEnabled]);
  const speakPara=(text,rateOverride)=>{
    stop();
    const rate=rateOverride||(isBedtimeAutoRead?0.8:SPEED_OPTIONS[speedRef.current].rate);
    const sents=text.match(/[^.!?]+[.!?]+/g)||[text];let si=0;
    const next=()=>{
      if(si>=sents.length){setSpeaking(false);setHlSent(-1);return;}
      const i=si;
      setHlSent(i);
      speak(sents[si], {
        rate,
        pitch: 0.8,
        onEnd: () => {
          si++;
          setTimeout(next,si<sents.length?500:0);
        }
      }).catch(() => {
        setSpeaking(false);
        setHlSent(-1);
      });
    };
    next();
  };
  useEffect(()=>{if(speaking)speakPara(curPara);},[paraIdx,speaking]);
  const toggleRead=()=>{if(speaking){stop();setSpeaking(false);setHlSent(-1);}else{setSpeaking(true);speakPara(curPara);}};
  const advance=()=>{
    stop();setSpeaking(false);setHlSent(-1);
    if(paraIdx+1>=paragraphs.length)setDone(true);else setParaIdx(p=>p+1);
  };
  const renderPara=(text)=>{const sents=text.match(/[^.!?]+[.!?]+/g)||[text];return sents.map((s,i)=><span key={i} className="rounded px-0.5 transition-colors" style={{background:hlSent===i?'#FFD16640':'transparent'}}>{s}{' '}</span>);};
  if(done)return <div className="px-6 py-16 text-center [animation:rqFadeIn_0.4s_both]">
    <div className="mb-3 text-7xl">🌙</div>
    <h2 className="mb-2 font-finley-display text-4xl text-finley-text">To be continued tomorrow...</h2>
    <p className="mb-6 text-base leading-7 text-finley-text-light">Sweet dreams, {profile?.name}! Finley will have a new chapter waiting for you.</p>
    <div className="mb-6 flex justify-center gap-1">{[0,1,2,3,4].map(i=><span key={i} className="inline-block text-3xl" style={{animation:`rqBounce 0.5s ${i*0.1}s both`}}>⭐</span>)}</div>
    <Btn onClick={onBack} color={C.secondary} full>Back to Library</Btn>
    <AnimatePresence>
      {showCoaching&&coachingPrompt&&(
        <ParentCoachingPrompt
          prompt={coachingPrompt}
          onDismiss={()=>setShowCoaching(false)}
          onLogDone={()=>{logCoachingDone();setShowCoaching(false);}}
        />
      )}
    </AnimatePresence>
  </div>;
  return <div className="flex h-full flex-col">
    <div className="flex shrink-0 items-center justify-between border-b border-finley-text-muted/15 px-5 py-4">
      <button onClick={()=>{stop();onBack();}} className="rounded-finley-md bg-finley-bg px-4 py-2 text-sm text-finley-text-light shadow-finley-sm">← Back</button>
      <div className="mx-3 flex-1 truncate text-center font-finley-display text-sm text-finley-text">{story?.title}</div>
      <button onClick={toggleRead} className="rounded-finley-md px-4 py-2 text-sm text-finley-text-light shadow-finley-sm" style={{background:speaking?C.accentLight:'var(--color-finley-bg)',border:speaking?`2px solid ${C.accent}`:'2px solid transparent'}}>{speaking?'⏸ Pause':'🔊 Read'}</button>
    </div>
    {!isBedtimeAutoRead&&speaking&&<div className="flex shrink-0 justify-center gap-1 border-b border-finley-text-muted/15 px-5 py-2">{SPEED_OPTIONS.map((s,i)=><button key={s.id} onClick={()=>{setSpeedIdx(i);speedRef.current=i;if(speaking){stop();setTimeout(()=>speakPara(curPara,s.rate),100);}}} className="rounded-finley-md px-3 py-1 text-xs" style={{border:speedIdx===i?`2px solid ${C.secondary}`:'2px solid transparent',background:speedIdx===i?C.secondaryLight:'transparent',color:speedIdx===i?C.secondary:C.textLight,fontWeight:speedIdx===i?700:400}}>{s.label}</button>)}</div>}
    <div className="flex-1 overflow-auto px-6 pb-4 pt-7" style={{WebkitOverflowScrolling:'touch'}}>
      <div className="mx-auto max-w-3xl text-balance text-[#1A1A2E]" style={{fontFamily:'var(--font-finley-story)',fontSize,lineHeight:1.8}}>{renderPara(curPara)}</div>
    </div>
    <div className="shrink-0 border-t border-finley-text-muted/15 px-5 pb-6 pt-3">
      <div className="mb-3 flex justify-center gap-1.5">{paragraphs.map((_,i)=><div key={i} className="h-2 rounded-finley-full transition-all" style={{width:i===paraIdx?20:8,background:i===paraIdx?C.secondary:'#E5E7EB'}} />)}</div>
      <button onClick={advance} className="w-full rounded-finley-lg px-4 py-4 font-finley-display text-base text-white shadow-finley-md" style={{background:`linear-gradient(135deg,${C.secondary},${C.purple})`}}>{paraIdx+1>=paragraphs.length?'🌙 Finish Story':'Next →'}</button>
    </div>
  </div>;
}
